import { useState, useEffect, useRef } from "react";
import { X, StickyNote, Phone, Mail, MapPin, User, Tag, Plus, Check, RotateCcw, UserPlus, MoreVertical, Pencil, Trash2 } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { formatTime } from "./chatUtils";

const TAG_COLORS = [
  "#6B7280", "#EF4444", "#F97316", "#EAB308",
  "#22C55E", "#06B6D4", "#3B82F6", "#8B5CF6", "#EC4899",
];

const InfoPanel = ({ conv, notes, onNoteAdded, onNoteUpdated, onNoteDeleted, onClose, onContactSaved }) => {
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [customerStatus, setCustomerStatus] = useState(conv.customer?.status ?? true);
  const [togglingStatus, setTogglingStatus] = useState(false);

  // Note edit/delete state
  const [noteMenu, setNoteMenu] = useState(null); // noteId of open 3-dot menu
  const [editingNote, setEditingNote] = useState(null); // { id, content }
  const [savingEdit, setSavingEdit] = useState(false);
  const noteMenuRef = useRef(null);

  // Save contact form state (shown when conv.customer is null)
  const [contactForm, setContactForm] = useState({ name: "", email: "", location: "" });
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    setCustomerStatus(conv.customer?.status ?? true);
  }, [conv.customer?.status]);

  // Close note 3-dot menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (noteMenuRef.current && !noteMenuRef.current.contains(e.target)) setNoteMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleEditNote = async (noteId) => {
    if (!editingNote?.content?.trim()) return;
    setSavingEdit(true);
    try {
      const r = await api.put(`/conversations/${conv.id}/notes/${noteId}`, { content: editingNote.content });
      onNoteUpdated(r.data.data);
      setEditingNote(null);
    } catch {
      toast.error("Failed to update note");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/conversations/${conv.id}/notes/${noteId}`);
      onNoteDeleted(noteId);
    } catch {
      toast.error("Failed to delete note");
    }
  };

  // Tags state
  const [customerTags, setCustomerTags] = useState(conv.customer?.tags || []);
  const [allTags, setAllTags] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [creatingTag, setCreatingTag] = useState(false);

  const customer = conv.customer;
  const name = customer?.name || conv.waId;
  const initials = name.slice(0, 2).toUpperCase();

  useEffect(() => {
    api.get("/tags").then((r) => setAllTags(r.data.data)).catch(() => {});
  }, []);

  const handleAddNote = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const r = await api.post(`/conversations/${conv.id}/notes`, { content });
      onNoteAdded(r.data.data);
      setContent("");
    } catch {
      toast.error("Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  const handleAssignTag = async (tag) => {
    if (!customer) return;
    const already = customerTags.find((t) => t.id === tag.id);
    try {
      if (already) {
        await api.delete(`/tags/customers/${customer.id}/tags/${tag.id}`);
        setCustomerTags((prev) => prev.filter((t) => t.id !== tag.id));
      } else {
        const r = await api.post(`/tags/customers/${customer.id}/tags`, { tagId: tag.id });
        setCustomerTags(r.data.data);
      }
    } catch {
      toast.error("Failed to update tag");
    }
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      const r = await api.post(`/conversations/${conv.id}/save-contact`, contactForm);
      toast.success(r.data.linked ? "Existing contact linked to this conversation" : "Contact saved successfully");
      onContactSaved(r.data.data);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to save contact");
    } finally {
      setSavingContact(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!customer) return;
    setTogglingStatus(true);
    try {
      await api.put(`/customers/${customer.id}`, { status: !customerStatus });
      setCustomerStatus((prev) => !prev);
      toast.success(customerStatus ? "Customer opted out" : "Customer re-subscribed");
    } catch {
      toast.error("Failed to update status");
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      const r = await api.post("/tags", { name: newTagName.trim(), color: newTagColor });
      const created = r.data.data;
      setAllTags((prev) => [...prev, created]);
      setNewTagName("");
      // Immediately assign the new tag to the customer
      if (customer) {
        const r2 = await api.post(`/tags/customers/${customer.id}/tags`, { tagId: created.id });
        setCustomerTags(r2.data.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to create tag");
    } finally {
      setCreatingTag(false);
    }
  };

  return (
    <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <span className="text-sm font-semibold text-gray-800">Details</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Customer Info / Save Contact */}
        <div className="p-4 bg-white border-b border-gray-200">
          {!customer ? (
            /* ── Unknown number — save contact form ── */
            <>
              <div className="flex items-center gap-2 mb-3">
                <UserPlus className="w-4 h-4 text-blue-500" />
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Save Contact</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 bg-gray-50 rounded-lg px-3 py-2">
                <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="font-medium text-gray-700">{conv.waId}</span>
              </div>
              <div className="space-y-2">
                <input
                  value={contactForm.name}
                  onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Name (optional)"
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  value={contactForm.email}
                  onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="Email (optional)"
                  type="email"
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <input
                  value={contactForm.location}
                  onChange={(e) => setContactForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="Location (optional)"
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  onClick={handleSaveContact}
                  disabled={savingContact}
                  className="w-full flex items-center justify-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {savingContact ? "Saving…" : "Save to Contacts"}
                </button>
              </div>
            </>
          ) : (
            /* ── Known customer — details view ── */
            <>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Customer</p>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
                  {customer?.name && <p className="text-xs text-gray-400 truncate">{conv.waId}</p>}
                  {customerStatus === false && (
                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium mt-1">
                      Opted out
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={handleToggleStatus}
                disabled={togglingStatus}
                className={`w-full flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-lg font-medium transition-colors mb-3 disabled:opacity-50 ${
                  customerStatus === false
                    ? "bg-green-100 text-green-700 hover:bg-green-200"
                    : "bg-red-50 text-red-600 hover:bg-red-100"
                }`}
              >
                <RotateCcw className="w-3 h-3" />
                {customerStatus === false ? "Re-enable (Opt-in)" : "Opt out customer"}
              </button>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>{conv.waId}</span>
                </div>
                {customer?.email && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{customer.email}</span>
                  </div>
                )}
                {customer?.location && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{customer.location}</span>
                  </div>
                )}
                {conv.assignedAgent && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{conv.assignedAgent.businessName}</span>
                  </div>
                )}
                <div className="text-xs text-gray-400 pt-1">
                  Last message: {formatTime(conv.lastMessageAt)}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Tags */}
        {customer && (
          <div className="p-4 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Tags</p>
              </div>
              <button
                onClick={() => setShowTagPicker((v) => !v)}
                className="text-gray-400 hover:text-blue-500 transition-colors"
                title="Manage tags"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Current tags */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {customerTags.length === 0 ? (
                <p className="text-xs text-gray-400">No tags</p>
              ) : (
                customerTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full text-white font-medium"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                    <button
                      onClick={() => handleAssignTag(tag)}
                      className="hover:opacity-75 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))
              )}
            </div>

            {/* Tag picker dropdown */}
            {showTagPicker && (
              <div className="border border-gray-200 rounded-lg bg-white shadow-sm mt-2">
                <div className="p-2 space-y-1 max-h-36 overflow-y-auto">
                  {allTags.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-2">No tags yet</p>
                  ) : (
                    allTags.map((tag) => {
                      const assigned = customerTags.find((t) => t.id === tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => handleAssignTag(tag)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 transition-colors"
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          <span className="text-xs text-gray-700 flex-1 text-left truncate">{tag.name}</span>
                          {assigned && <Check className="w-3 h-3 text-blue-500 flex-shrink-0" />}
                        </button>
                      );
                    })
                  )}
                </div>
                {/* Create new tag */}
                <div className="border-t border-gray-100 p-2">
                  <p className="text-xs text-gray-400 mb-1.5">Create new tag</p>
                  <div className="flex gap-1.5 mb-1.5">
                    {TAG_COLORS.map((c) => (
                      <button
                        key={c}
                        onClick={() => setNewTagColor(c)}
                        className="w-4 h-4 rounded-full flex-shrink-0 ring-offset-1 transition-all"
                        style={{
                          backgroundColor: c,
                          boxShadow: newTagColor === c ? `0 0 0 2px white, 0 0 0 3px ${c}` : "none",
                        }}
                      />
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <input
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleCreateTag()}
                      placeholder="Tag name..."
                      className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                    <button
                      onClick={handleCreateTag}
                      disabled={creatingTag || !newTagName.trim()}
                      className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded disabled:opacity-50 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <StickyNote className="w-3.5 h-3.5 text-yellow-500" />
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Internal Notes</p>
          </div>
          <div className="space-y-2">
            {notes.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No notes yet</p>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5">
                  {editingNote?.id === note.id ? (
                    /* ── Inline edit mode ── */
                    <div>
                      <textarea
                        value={editingNote.content}
                        onChange={(e) => setEditingNote((prev) => ({ ...prev, content: e.target.value }))}
                        rows={3}
                        className="w-full text-xs border border-yellow-300 rounded-lg px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
                        autoFocus
                      />
                      <div className="flex gap-1.5 mt-1.5">
                        <button
                          onClick={() => handleEditNote(note.id)}
                          disabled={savingEdit || !editingNote.content.trim()}
                          className="flex-1 text-xs bg-yellow-400 hover:bg-yellow-500 text-white py-1 rounded-lg disabled:opacity-50 font-medium transition-colors"
                        >
                          {savingEdit ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={() => setEditingNote(null)}
                          className="flex-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 py-1 rounded-lg font-medium transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Normal view ── */
                    <>
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-xs text-gray-700 whitespace-pre-wrap flex-1">{note.content}</p>
                        <div className="relative flex-shrink-0" ref={noteMenu === note.id ? noteMenuRef : null}>
                          <button
                            onClick={() => setNoteMenu((prev) => (prev === note.id ? null : note.id))}
                            className="p-0.5 rounded hover:bg-yellow-200 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                          {noteMenu === note.id && (
                            <div className="absolute right-0 top-5 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden w-28">
                              <button
                                onClick={() => { setEditingNote({ id: note.id, content: note.content }); setNoteMenu(null); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Pencil className="w-3 h-3 text-gray-400" />
                                Edit
                              </button>
                              <button
                                onClick={() => { setNoteMenu(null); handleDeleteNote(note.id); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-1.5 flex justify-between items-center">
                        <span className="text-xs text-gray-500">{note.author?.businessName || "Agent"}</span>
                        <span className="text-xs text-gray-400">{formatTime(note.createdAt)}</span>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add note */}
      <div className="p-3 border-t border-gray-200 bg-white">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add a note..."
          rows={3}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
        <button
          onClick={handleAddNote}
          disabled={saving || !content.trim()}
          className="mt-2 w-full text-sm bg-yellow-400 hover:bg-yellow-500 text-white font-medium py-1.5 rounded-lg disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving..." : "Add Note"}
        </button>
      </div>
    </div>
  );
};

export default InfoPanel;
